import type { CheckResult } from './types';

/**
 * The Results screen's filter, ported from Android's `ui/result/ResultFilter.kt`.
 *
 * Lives in `lib/` rather than in the screen for the same reason it is a separate
 * file on Android: it is the one part of that screen that can be wrong in a way
 * nobody sees. A filter that quietly drops rows looks exactly like a short list.
 *
 * The values are lower-case because they are also the suffixes of the string
 * keys the segmented buttons render (`filter_all` / `filter_alive` /
 * `filter_dead`), so a tab and its label cannot drift apart.
 */
export type ResultFilter = 'all' | 'alive' | 'dead';

/**
 * Bar order — the port of the Kotlin enum's declaration order, which is what
 * Android's segmented row iterates. Kept as data so the screen does not hard-code
 * the order a second time.
 */
export const RESULT_FILTERS = ['all', 'alive', 'dead'] as const satisfies readonly ResultFilter[];

/**
 * Pure filtering, so the screen only chooses a value and never decides anything.
 *
 * `'all'` returns the caller's own array rather than a copy, exactly as Android
 * returns the same list: the unfiltered case is the common one and re-allocating
 * a 200-row list on every render buys nothing.
 *
 * A mixed-content row (a plain `http://` link Safari refuses to load) is
 * `unhealthy` in the data and therefore lands under `'dead'` here, even though
 * the tile that draws it says "not measurable" rather than "dead" (SPEC §7).
 * That is deliberate: the filter partitions what was actually submitted, so the
 * three tab counts always add up to the batch the backend received. Splitting
 * mixed-content out into a fourth bucket here would make the screen disagree
 * with the payload.
 */
export function filterResults(results: CheckResult[], filter: ResultFilter): CheckResult[] {
  switch (filter) {
    case 'all':
      return results;
    case 'alive':
      return results.filter((result) => result.healthStatus === 'healthy');
    case 'dead':
      return results.filter((result) => result.healthStatus === 'unhealthy');
  }
}

/**
 * The number of rows behind each tab, in one pass over the batch.
 *
 * Deliberately the **same two predicates** `filterResults` uses, and in the same
 * file, because the counts and the lists have to agree: a bar reading `Dead 1`
 * over a Dead tab showing nothing is the exact shape of a filter that quietly
 * drops rows, and it is the failure this module exists to make impossible.
 *
 * Counted from the whole batch, never from the filtered list — the three numbers
 * have to add up to what was submitted no matter which tab is active. A
 * mixed-content row lands under `dead` here for the reason spelled out above:
 * this partitions what the backend actually received, and the screen must not
 * disagree with the payload even where it draws that row amber.
 */
export function countResults(results: readonly CheckResult[]): Record<ResultFilter, number> {
  let alive = 0;
  let dead = 0;
  for (const result of results) {
    if (result.healthStatus === 'healthy') alive += 1;
    else if (result.healthStatus === 'unhealthy') dead += 1;
  }
  return { all: results.length, alive, dead };
}
